import React, { useState, useEffect, useRef } from "react";
import { socket } from "../socket";
import { PlayerAvatar, PlayerName, bannerStyle } from "./PlayerIdentity";
import { t } from "../i18n";
import { useSounds, useCountdownTick } from "../sound";
import { alertDialog } from "../dialog";

// Pixel Guess : une image très pixelisée se révèle par paliers. Le premier à
// buzzer fige l'image et tente sa réponse ; s'il se trompe, son buzzer est
// bloqué quelques secondes et les autres reprennent la main.
//
// Le serveur n'envoie que l'image du palier atteint — jamais l'originale avant
// la révélation. Le client n'a donc rien de plus net à afficher que ce qu'il
// montre, quoi qu'on inspecte dans le navigateur.
function PixelGuess({ gameId, player }) {
  const [round, setRound]     = useState({ index: 0, total: 0 });
  const [image, setImage]     = useState(null);
  const [level, setLevel]     = useState(0);
  const [levels, setLevels]   = useState(8);
  const [hint, setHint]       = useState(null);
  const [players, setPlayers] = useState([]);
  const [buzzedBy, setBuzzed] = useState(null);
  const [answerEnd, setAnsEnd] = useState(null);
  const [reveal, setReveal]   = useState(null);
  const [guess, setGuess]     = useState("");
  const [lockedUntil, setLock] = useState(0);
  const [scoring, setScoring] = useState("points");
  const [now, setNow]         = useState(Date.now());

  const myId = player?.playerId;
  const myTurn = buzzedBy === myId;
  const { playPop, playSuccess, playFail } = useSounds();
  const inputRef = useRef(null);

  useEffect(() => {
    const applyRound = (s) => {
      setRound({ index: s.index, total: s.total });
      setImage(s.image); setLevel(s.level); setLevels(s.levels || 8);
      setHint(s.hint); setPlayers(s.players); setScoring(s.scoring || "points");
      setBuzzed(null); setAnsEnd(null); setReveal(null); setGuess("");
    };
    socket.on("pgRound", applyRound);
    socket.on("pgState", (s) => {
      applyRound(s);
      setBuzzed(s.buzzedBy);
      if (s.revealed) setReveal({ answer: s.revealed });
      if (s.lockedFor > 0) setLock(Date.now() + s.lockedFor);
    });
    socket.on("pgImage", ({ level, image }) => { setLevel(level); setImage(image); });
    socket.on("pgBuzzed", ({ playerId, answerMs, players }) => {
      playPop();
      setBuzzed(playerId); setAnsEnd(Date.now() + answerMs); setPlayers(players);
    });
    socket.on("pgWrong", ({ playerId, lockedFor, players }) => {
      playFail();
      setBuzzed(null); setAnsEnd(null); setPlayers(players); setGuess("");
      // On resynchronise l'horloge en même temps que le verrou : sinon le
      // premier rendu part d'un `now` vieux de 200 ms et affiche une seconde
      // de trop.
      if (playerId === myId) { setNow(Date.now()); setLock(Date.now() + lockedFor); }
    });
    socket.on("pgReveal", (r) => {
      (r.winnerId ? playSuccess : playPop)();
      setReveal(r); setImage(r.image); setPlayers(r.players);
      setBuzzed(null); setAnsEnd(null); setGuess("");
    });
    socket.on("pgError", ({ error }) => alertDialog(error));

    socket.emit("pgGetState", { gameId });
    return () => {
      ["pgRound", "pgState", "pgImage", "pgBuzzed", "pgWrong", "pgReveal", "pgError"]
        .forEach(e => socket.off(e));
    };
  }, [gameId, myId, playPop, playSuccess, playFail]);

  // Une seule horloge, tant qu'il y a un compte à rebours ou un blocage à afficher
  useEffect(() => {
    if (!answerEnd && lockedUntil <= Date.now()) return;
    const iv = setInterval(() => setNow(Date.now()), 200);
    return () => clearInterval(iv);
  }, [answerEnd, lockedUntil]);

  useEffect(() => { if (myTurn) inputRef.current?.focus(); }, [myTurn]);

  const locked = lockedUntil > now;
  const canBuzz = !buzzedBy && !reveal && !locked;

  const buzz = () => { if (canBuzz) socket.emit("pgBuzz", { gameId }); };
  const send = (e) => {
    e.preventDefault();
    if (!myTurn || !guess.trim()) return;
    socket.emit("pgGuess", { gameId, guess });
  };

  // Espace pour buzzer : le mode se joue à la vitesse, viser un bouton coûte
  // trop cher. Neutralisé pendant la saisie, sinon on ne pourrait plus écrire.
  useEffect(() => {
    const onKey = (e) => {
      if (e.code !== "Space" || e.target.tagName === "INPUT") return;
      e.preventDefault();
      buzz();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const answerLeft = answerEnd ? Math.max(0, Math.ceil((answerEnd - now) / 1000)) : 0;
  const lockLeft   = locked ? Math.ceil((lockedUntil - now) / 1000) : 0;
  const buzzer     = players.find(p => p.playerId === buzzedBy);

  // Le compte à rebours de réponse ne concerne que celui qui a buzzé
  useCountdownTick(answerLeft, { active: !!answerEnd && myTurn });

  if (!image) {
    return <div className="PixelGuess"><p className="pg-waiting">{t("pixelGuess.waiting")}</p></div>;
  }

  return (
    <div className="PixelGuess">
      <div className="pg-header">
        <span className="pg-progress">{t("pixelGuess.roundOf", { index: round.index + 1, total: round.total })}</span>
        {hint && <span className="pg-hint">{hint}</span>}
        <span className="pg-sharpness" title={t("pixelGuess.sharpness")}>
          {Array.from({ length: levels }, (_, i) => (
            <span key={i} className={`pg-step${i <= level ? " on" : ""}`} />
          ))}
        </span>
      </div>

      <div className="pg-stage">
        {/* image-rendering: pixelated — sans cela le navigateur lisse les blocs
            et l'image devient plus lisible qu'elle ne devrait l'être. */}
        <img src={image} alt="" className={`pg-image${reveal ? " clear" : ""}`} />
        {buzzedBy && !reveal && (
          <div className="pg-overlay">
            <p className="pg-overlay-name">
              {myTurn ? t("pixelGuess.yourBuzz") : t("pixelGuess.theirBuzz", { pseudo: buzzer?.pseudo || "?" })}
            </p>
            <p className="pg-overlay-timer">{answerLeft}</p>
          </div>
        )}
      </div>

      {reveal ? (
        <p className="pg-reveal">
          {reveal.winnerId
            ? t("pixelGuess.foundBy", { pseudo: reveal.winnerPseudo, answer: reveal.answer, points: reveal.points })
            : t("pixelGuess.nobody", { answer: reveal.answer })}
        </p>
      ) : myTurn ? (
        <form className="pg-guess-row" onSubmit={send}>
          <input ref={inputRef} className="pg-guess" value={guess} autoComplete="off"
            onChange={e => setGuess(e.target.value)} placeholder={t("pixelGuess.guessPlaceholder")} />
          <button type="submit" className="btn btn-primary pg-send" disabled={!guess.trim()}>
            {t("pixelGuess.send")}
          </button>
        </form>
      ) : (
        <button type="button" className={`pg-buzzer${canBuzz ? "" : " disabled"}`}
          disabled={!canBuzz} onClick={buzz}>
          {locked ? t("pixelGuess.lockedFor", { s: lockLeft })
            : buzzedBy ? t("pixelGuess.taken")
            : t("pixelGuess.buzz")}
        </button>
      )}

      <div className="pg-players">
        {players.map(p => (
          <div key={p.playerId} style={bannerStyle(p)}
            className={`pg-player${p.playerId === myId ? " me" : ""}`
              + `${p.buzzed ? " buzzed" : ""}${p.lockedFor > 0 ? " locked" : ""}`
              + `${p.offline ? " offline" : ""}`}>
            <PlayerAvatar player={p} size={26} />
            <PlayerName player={p} className="pg-player-name" />
            <span className="pg-player-score">
              {scoring === "wins" ? t("pixelGuess.winCount", { n: p.wins }) : p.score}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default PixelGuess;
